import { Auth } from 'aws-amplify';
import { userFetchResponse, userLogoutResponse } from 'store/actions/userReducers';
import { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { AppointmentSchema, UserSchema } from 'vl-common/src/schemas/shared';
import { PolicySchema } from 'vl-common/src/schemas/shared/policy';
import qs from 'query-string';
import { type Dispatch } from 'react-redux';
import * as actionTypes from './actionTypes';
import { fetchData, requestSuccess, unpackJson } from './action';
import { getRuntimeConfig } from 'vl-common/src/hooks/Runtime';

// If this returns undefined it means the user is not logged in or there has been a Network failure.
// In both cases there's no recovery action and the user needs to be pushed back to the login page.
// (The user will *not* have been warned if there has been a networking problem).
export async function tryGetUser(source_url?: string) {
  try {
    const tokens = await Auth.currentSession();
    const token = tokens.getIdToken().getJwtToken();
    const userGuid = tokens.getIdToken().payload?.sub;
    const { API_URL } = getRuntimeConfig();

    const url = new URL(`${API_URL}/users/user/${userGuid}`);
    source_url && url.searchParams.set('source_url', source_url);

    const ret = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (ret.ok && ret.status === 200) {
      return await unpackJson(ret, UserSchema);
    }
  } catch {
    // fall through
  }

  return undefined;
}

export const userSendToken = (userGuid: string, token: string) => async (dispatch: Dispatch) => {
  await dispatch(userLogout());

  const user = await Auth.signIn(userGuid, undefined, { authFlowType: 'CUSTOM_WITHOUT_SRP' });

  if (user.challengeName === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
    try {
      const challengeResponse = await Auth.sendCustomChallengeAnswer(user, token);

      if (challengeResponse?.additionalInfoRequiredField === 'code2') {
        return user;
      }
    } catch (e) {
      console.error(e);
    }
  }

  throw dispatch({ type: actionTypes.AUTH_LOGIN_FAIL, user }, false);
};

export const userHasLoggedOut = () => (dispatch: Dispatch) =>
  fetchData({ params: 'users/logout/APPUSER', method: 'PUT', postBody: {} })(dispatch, () => {}, null) as {
    logout: { logout_urls: string[] };
  };

export const userLogout = (queryClient?: QueryClient) => async (dispatch: Dispatch) => {
  await Auth.currentSession().catch(() => {});

  // this needs to be actioned first in order to stop corrupt requests being made. See VL2-2174
  // JAE this stops you from being able to login twice
  // dispatch(requestSuccess(actionTypes.AUTH_SIGNED_OUT));

  await dispatch(userHasLoggedOut())
    .then((logoutResponse) => {
      const userIsUsingSso = logoutResponse.logout;
      if (userIsUsingSso) {
        const { logout } = logoutResponse;
        logout.logout_urls.every(async (logoutUrl) => {
          try {
            const res = await fetch(logoutUrl, {
              method: 'GET',
              headers: {
                'Cache-Control': 'no-store'
              }
            });
            const blob = await res.blob();
            const urlObject = URL.createObjectURL(blob);
            const logoutIframe = document.createElement('iframe');
            logoutIframe.setAttribute('src', urlObject);
            logoutIframe.setAttribute('style', 'position: absolute;width:0;height:0;border:0;');
            document.body.appendChild(logoutIframe);
          } catch {
            // Best effort until we get proper URLs from AXA
          }
          return true;
        });
      }
    })
    .catch();

  await Auth.signOut().catch(() => {});

  // remove the user data from Redux store
  dispatch(userFetchResponse(actionTypes.AUTH_SUCCESS, null, null, false));

  // clear the user from the redux store
  dispatch(requestSuccess(actionTypes.AUTH_LOGOUT_SUCCESS, { user: null }));
  if (queryClient) {
    queryClient
      .getQueriesData({ queryKey: ['shared-websocket'] })
      .filter(([, qd]) => qd)
      .forEach(([, qd]: any) => {
        console.log('Closing shared-websocket', qd);
        qd.close();
      });
    queryClient
      .getQueriesData({ queryKey: ['useAppointmentEvents'] })
      .filter(([, qd]) => qd)
      .forEach(([, qd]: any) => {
        console.log('Closing useAppointmentEvents web socket', qd);
        qd.close();
      });
    queryClient.clear();
  }

  return dispatch(userLogoutResponse());
};

export const checkUser = (email: string) => {
  const options = {
    params: 'check-user',
    method: 'POST',
    nobearer: true,
    postBody: { email }
  };
  return fetchData(options);
};

export const userNewPassword = (email: string, password: string) => {
  const options = {
    params: 'new-password',
    method: 'POST',
    nobearer: true,
    postBody: {
      email,
      password
    },
    actionType: actionTypes.AUTH_LOGIN_NEWPASSWORD_SUCCESS,
    errorType: actionTypes.AUTH_LOGIN_NEWPASSWORD_FAIL
  };
  return fetchData(options);
};

export const checkPassword = (password: string) => {
  const options = {
    params: 'check-password',
    method: 'PUT',
    nobearer: true,
    postBody: {
      pass: password
    }
  };
  return fetchData(options);
};

export const userResetPassword = (email: string) => {
  const { APP_CLIENT_ID } = getRuntimeConfig();
  const options = {
    params: 'forgot-password',
    method: 'POST',
    nobearer: true,
    postBody: {
      ClientId: APP_CLIENT_ID,
      Username: email
    }
  };
  return fetchData(options);
};

export const userResetPasswordConfirm = (request: { email: string; code: string; password: string }) => {
  const options = {
    params: 'reset-password',
    method: 'POST',
    nobearer: true,
    postBody: request,
    actionType: actionTypes.AUTH_LOGIN_RESETPASSWORD_CONFIRM_SUCCESS,
    errorType: actionTypes.AUTH_LOGIN_RESETPASSWORD_CONFIRM_FAIL
  };
  return fetchData(options);
};

// User Appointment

type GetUserAppointmentRequest = {
  page?: number;
  rows?: number;
  sort?: string;
  order?: string;
  case?: number;
  client_case_id?: string;
  patient?: string;
  clinician?: string;
  date_start?: string;
  date_end?: string;
  week_offset?: number;
  completed?: string;
  status?: string;
  active?: string;
  cancelled?: boolean;
  appt_id?: number;
  upcoming?: boolean;
  cat_code?: string;
  cat_name?: string;
  area_code?: string;
  area_name?: string;
  clin_type?: string;
  client_code?: string;
  pat_guid?: string;
  created_from?: string;
  updated_from?: string;
  rescheduled?: boolean;
  team?: boolean;
  call_type?: string;
  allow_teams?: 1 | 0;
};

export const getUserAppointment = (
  type:
    'USER_HISTORY_APPOINTMENT' | 'USER_UPCOMINGHISTORY' | 'USER_APPOINTMENTS' | 'USER_DIARY' | 'USER_NEXT_APPOINTMENT',
  request: GetUserAppointmentRequest
) => {
  const options = {
    params: `booking/appointments/APPUSER?${qs.stringify(request)}`,
    actionType: `${type}_SUCCESS`,
    errorType: `${type}_FAIL`
  };
  return fetchData(options, z.array(AppointmentSchema));
};

export const getClinicianAppointmentById = (appointmentId: number, cacheBuster: number = Date.now()) => {
  const options = {
    params: `clinicians/appt/metadata/APPUSER?appt_id=${appointmentId}&_cb=${cacheBuster}`
  };
  return fetchData(options, AppointmentSchema);
};

export const getUserAppointmentWithBreaks = (
  type: string,
  request: {
    rows: number;
    cancelled: string;
    sort: string;
    date_start?: string;
    date_end?: string;
    week_offset?: number;
  }
) => {
  const options = {
    params: `clinicians/diary/APPUSER?${qs.stringify(request)}`,
    actionType: `${type}_SUCCESS`,
    errorType: `${type}_FAIL`
  };
  return fetchData(options);
};

/* Timeline Appointment History */
export const userTimelineHistory = (type: string, caseID: string) => {
  const options = {
    params: `users/timeline/APPUSER?case_id=${caseID}`,
    actionType: `${type}_SUCCESS`,
    errorType: `${type}_FAIL`
  };
  return fetchData(options);
};

export const getAvatar = (ID: string) => {
  const options = {
    params: `avatar?user_guid=${ID}`
  };
  return fetchData(options);
};

export const getSignature = (ID: string) => {
  const options = {
    params: `signature?user_guid=${ID}`
  };
  return fetchData(options);
};

/* User Policy */
export const acceptUserPolicy = (request: { user_guid: string; policy_code: string; accepted: boolean }) => {
  return fetchData({
    params: 'users/policy',
    method: 'POST',
    postBody: request,
    actionType: 'USER_POLICY_ACCEPTED_SUCCESS',
    errorType: 'USER_POLICY_ACCEPTED_FAIL'
  });
};

export const userPolicy = (type: string) => {
  const options = {
    params: 'users/policy/APPUSER',
    actionType: `${type}_SUCCESS`,
    errorType: `${type}_FAIL`
  };
  return fetchData(options, z.array(PolicySchema));
};

export const patientCasePolicies = (patient_guid: string) => {
  const options = {
    params: `users/policy/${patient_guid}/CASE`,
    METHOD: 'GET'
  };
  return fetchData(options);
};

export const allPatientPolicies = () =>
  fetchData(
    {
      params: 'user-policies',
      nobearer: true,
      method: 'GET'
    },
    z.object({
      policies: z.array(PolicySchema)
    })
  );

export const createSelf = (
  first: string,
  last: string,
  email: string,
  password: string,
  captcha?: string,
  self_refer = false,
  clientId = 2,
  type = 'CREATE_SELF'
) => {
  const options = {
    params: 'create-self',
    method: 'POST',
    nobearer: true,
    actionType: `${type}_SUCCESS`,
    errorType: `${type}_FAIL`,
    customHeaders: {
      'x-aws-waf-token': captcha || ''
    },
    credentialsProp: 'include' as const,
    postBody: {
      first_name: first,
      last_name: last,
      email,
      password,
      client_id: clientId,
      self_refer
    }
  };
  return fetchData(options);
};

export const getUserTourData = () => {
  const options = {
    params: 'users/tour/APPUSER',
    method: 'GET'
  };
  return fetchData(options);
};

export const getSSOData = (sso_id: string, membership_no: string) => {
  const options = {
    params: 'axadata',
    method: 'POST',
    postBody: {
      sso_id,
      membership_no
    }
  };
  return fetchData(options);
};
